<template>
  <Teleport to="body">
    <div v-if="isActive" class="tutorial-overlay">
      <!-- Tooltip positioned based on current step -->
      <div class="tutorial-tooltip" :class="tooltipPositionClass">
        <!-- Step indicator -->
        <div class="step-indicator">
          <span v-for="i in totalSteps" :key="i" class="step-dot" :class="{ active: i - 1 === currentStepIndex }"></span>
        </div>

        <!-- Title -->
        <h3 class="tooltip-title">{{ currentStep?.title }}</h3>

        <!-- Content -->
        <ul class="tooltip-content">
          <li v-for="(line, idx) in currentStep?.content" :key="idx">{{ line }}</li>
        </ul>

        <!-- Twitter button (for CTA step) -->
        <a
          v-if="currentStep?.showTwitterButton"
          href="https://x.com/megadotarmy"
          target="_blank"
          rel="noopener noreferrer"
          class="twitter-btn"
        >
          <svg class="twitter-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
          </svg>
          Follow @megadotarmy
        </a>

        <!-- Navigation -->
        <div class="tooltip-nav">
          <button class="nav-btn skip-btn" @click="skipTutorial">Exit</button>
          <div class="nav-right">
            <button v-if="!isFirstStep" class="nav-btn back-btn" @click="prevStep">Back</button>
            <button class="nav-btn next-btn" @click="nextStep">
              {{ isLastStep ? 'Done' : 'Next' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { watch, onUnmounted, computed } from 'vue';
import { useTutorial } from '@/stores/tutorialState';
import type { TutorialHighlight } from '@/stores/tutorialState';

const {
  isActive,
  currentStepIndex,
  currentStep,
  totalSteps,
  isFirstStep,
  isLastStep,
  currentHighlights,
  nextStep,
  prevStep,
  skipTutorial
} = useTutorial();

// Compute tooltip position class based on what's highlighted
const tooltipPositionClass = computed(() => {
  const highlights = currentHighlights.value;

  // Step 1: Circles highlighted - position at bottom right
  if (highlights.includes('circles') && !highlights.includes('sequencer')) {
    return 'position-bottom-right';
  }

  // Step 2: Flow (circles + sequencer + chart) - position at left center
  if (highlights.includes('sequencer')) {
    return 'position-left';
  }

  // Step 3: Stats highlighted - position at bottom center (below header)
  if (highlights.includes('stats')) {
    return 'position-below-header';
  }

  // Step 4: Enlist highlighted - position at bottom center (below button)
  if (highlights.includes('enlist')) {
    return 'position-below-header';
  }

  // Default: center
  return 'position-center';
});

// Apply tutorial highlight classes to body
const applyHighlights = (highlights: TutorialHighlight[]) => {
  // Remove all tutorial classes first
  document.body.classList.remove(
    'tutorial-active',
    'tutorial-highlight-circles',
    'tutorial-highlight-sequencer',
    'tutorial-highlight-chart',
    'tutorial-highlight-stats',
    'tutorial-highlight-enlist',
    'tutorial-highlight-search'
  );

  if (highlights.length === 0) return;

  document.body.classList.add('tutorial-active');

  for (const highlight of highlights) {
    if (highlight !== 'none') {
      document.body.classList.add(`tutorial-highlight-${highlight}`);
    }
  }
};

// Watch for tutorial state changes
watch([isActive, currentHighlights], ([active, highlights]) => {
  if (active) {
    applyHighlights(highlights);
  } else {
    // Remove all tutorial classes when inactive
    document.body.classList.remove(
      'tutorial-active',
      'tutorial-highlight-circles',
      'tutorial-highlight-sequencer',
      'tutorial-highlight-chart',
      'tutorial-highlight-stats',
      'tutorial-highlight-enlist',
      'tutorial-highlight-search'
    );
  }
}, { immediate: true });

// Cleanup on unmount
onUnmounted(() => {
  document.body.classList.remove(
    'tutorial-active',
    'tutorial-highlight-circles',
    'tutorial-highlight-sequencer',
    'tutorial-highlight-chart',
    'tutorial-highlight-stats',
    'tutorial-highlight-enlist',
    'tutorial-highlight-search'
  );
});
</script>

<style scoped>
.tutorial-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tutorial-tooltip {
  position: absolute;
  width: 400px;
  max-width: calc(100vw - 40px);
  background: var(--bg-surface);
  border: 2px solid var(--accent-primary);
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 60px rgba(16, 185, 129, 0.3);
  pointer-events: auto;
  transition: all 0.3s ease;
  z-index: 99999;
}

/* Position classes */
.position-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.position-bottom-right {
  bottom: 80px;
  right: 40px;
  top: auto;
  left: auto;
  transform: none;
}

.position-left {
  top: 50%;
  left: 40px;
  right: auto;
  transform: translateY(-50%);
}

.position-below-header {
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
}

/* Step indicator dots */
.step-indicator {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
}

.step-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--border);
  transition: all 0.3s ease;
}

.step-dot.active {
  background: var(--accent-primary);
  transform: scale(1.4);
  box-shadow: 0 0 12px var(--accent-primary);
}

/* Title */
.tooltip-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--accent-primary);
  margin: 0 0 20px 0;
  text-align: center;
}

/* Content list */
.tooltip-content {
  margin: 0 0 24px 0;
  padding: 0 0 0 24px;
  list-style: disc;
}

.tooltip-content li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 12px;
}

.tooltip-content li:last-child {
  margin-bottom: 0;
}

/* Twitter button */
.twitter-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  margin-bottom: 20px;
  background: var(--text-primary);
  color: var(--bg-primary);
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
}

.twitter-btn:hover {
  opacity: 0.9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.twitter-icon {
  width: 20px;
  height: 20px;
}

/* Navigation */
.tooltip-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

.nav-right {
  display: flex;
  gap: 10px;
}

.nav-btn {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.skip-btn {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-muted);
}

.skip-btn:hover {
  border-color: var(--text-secondary);
  color: var(--text-secondary);
}

.back-btn {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-secondary);
}

.back-btn:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.next-btn {
  background: var(--accent-primary);
  border: 1px solid var(--accent-primary);
  color: var(--bg-primary);
}

.next-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .tutorial-tooltip {
    width: calc(100vw - 32px);
    padding: 20px;
  }

  .position-bottom-right {
    bottom: 20px;
    right: 16px;
    left: 16px;
    width: auto;
  }

  .position-left {
    left: 16px;
    right: 16px;
    width: auto;
  }

  .tooltip-title {
    font-size: 18px;
  }

  .tooltip-content li {
    font-size: 13px;
  }

  .nav-btn {
    padding: 10px 18px;
    font-size: 13px;
  }
}
</style>
